import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { color, fontSize, fontWeight, radius, shadow, zIndex } from '@/design/tokens';
import { useT } from '@/i18n';
import { api, type Page } from '@/lib/api';
import { Button } from '@/design/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/design/states';

/**
 * The notification centre (NOTIF-005).
 *
 * Two behaviours here are requirements rather than choices:
 *
 * - A `isClinicalCritical` notification cannot be dismissed, only acknowledged.
 *   PRD 15.2 forbids silently hiding a critical clinical alert, so the dismiss
 *   control is absent rather than disabled - a disabled control invites
 *   pressing it.
 * - Read/dismiss/snooze state is written to the server, not held locally, so a
 *   doctor who acknowledges a critical potassium result on the ward tablet does
 *   not see it again on the consulting-room desktop (NOTIF-004).
 */

type Severity = 'INFO' | 'SUCCESS' | 'WARNING' | 'CRITICAL';

interface NotificationItem {
  id: string;
  type: string;
  severity: Severity;
  title: string;
  body: string | null;
  actionRoute: string | null;
  isClinicalCritical: boolean;
  readAt: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
  /** Pre-formatted by the server in the tenant's zone and the user's locale. */
  relativeTime: string;
}

const SEVERITY_STYLE: Record<
  Severity,
  { border: string; bg: string; fg: string; icon: string; label: string }
> = {
  CRITICAL: {
    border: color.status.danger.border,
    bg: color.status.danger.bg,
    fg: color.status.danger.fg,
    icon: '▲',
    label: 'حرج · يتطلب إقراراً',
  },
  WARNING: {
    border: color.border.default,
    bg: color.surface.card,
    fg: color.status.warning.fg,
    icon: '!',
    label: 'تحذير',
  },
  SUCCESS: {
    border: color.border.default,
    bg: color.surface.card,
    fg: color.status.success.fg,
    icon: '✓',
    label: 'تم',
  },
  INFO: {
    border: color.border.default,
    bg: color.surface.card,
    fg: color.status.info.fg,
    icon: 'i',
    label: 'معلومة',
  },
};

type Filter = 'all' | 'critical' | 'finance';

export function NotificationDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () =>
      api.get<Page<NotificationItem>>('/notifications', {
        query: { filter, pageSize: 30 },
      }),
    enabled: open,
    // Polling fallback: the realtime channel is an optional dependency and the
    // notification centre must work without it (FLOW-009, PRD 21.1).
    refetchInterval: open ? 30_000 : false,
  });

  const acknowledge = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/acknowledge`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/dismiss`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  if (!open) return null;

  const items = data?.items ?? [];

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,.35)',
          zIndex: zIndex.drawerScrim,
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('app.notifications')}
        style={{
          position: 'fixed',
          insetBlock: 0,
          insetInlineStart: 0,
          height: '100vh',
          width: '400px',
          maxWidth: '100vw',
          background: color.surface.card,
          zIndex: zIndex.drawer,
          boxShadow: shadow.drawer,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: '18px 20px',
            borderBottom: `1px solid ${color.border.subtle}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2 style={{ fontSize: fontSize.xl, fontWeight: fontWeight.black, margin: 0 }}>
            {t('app.notifications')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('action.close')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: color.text.muted,
            }}
          >
            ✕
          </button>
        </header>

        <div
          style={{
            display: 'flex',
            gap: '6px',
            padding: '12px 20px',
            borderBottom: `1px solid ${color.border.subtle}`,
          }}
        >
          {(
            [
              ['all', t('app.notifications.all')],
              ['critical', t('app.notifications.critical')],
              ['finance', t('app.notifications.finance')],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              aria-pressed={filter === key}
              style={{
                padding: '5px 11px',
                borderRadius: radius.sm,
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: fontSize.sm,
                background: filter === key ? color.brand.soft : 'transparent',
                color: filter === key ? color.brand.primary : color.text.muted,
                fontWeight: filter === key ? fontWeight.bold : fontWeight.medium,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {isLoading ? <LoadingState /> : null}
          {isError ? <ErrorState onRetry={() => void refetch()} /> : null}
          {!isLoading && !isError && items.length === 0 ? (
            <EmptyState title={t('app.notifications.empty')} icon="⚑" />
          ) : null}

          {items.map((item) => {
            const style = SEVERITY_STYLE[item.severity];
            const isRead = Boolean(item.readAt);
            const needsAck = item.isClinicalCritical && !item.acknowledgedAt;

            return (
              <article
                key={item.id}
                style={{
                  border: `1px solid ${style.border}`,
                  background: style.bg,
                  borderRadius: radius.lg,
                  padding: '13px 14px',
                  opacity: isRead && !needsAck ? 0.7 : 1,
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '5px' }}
                >
                  <span aria-hidden style={{ color: style.fg, fontWeight: fontWeight.black }}>
                    {style.icon}
                  </span>
                  <span
                    style={{ fontSize: fontSize.xs, fontWeight: fontWeight.black, color: style.fg }}
                  >
                    {style.label}
                  </span>
                </div>
                <div
                  style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, lineHeight: 1.5 }}
                >
                  {item.title}
                </div>
                {item.body ? (
                  <div style={{ fontSize: fontSize.sm, color: color.text.muted, marginTop: '4px' }}>
                    {item.body}
                  </div>
                ) : null}
                <div style={{ fontSize: fontSize.sm, color: color.text.faint, marginTop: '4px' }}>
                  {item.relativeTime}
                </div>

                <div style={{ display: 'flex', gap: '7px', marginTop: '10px' }}>
                  {item.actionRoute ? (
                    <Button
                      size="sm"
                      variant={needsAck ? 'danger' : 'secondary'}
                      onClick={() => {
                        navigate(item.actionRoute!);
                        onClose();
                      }}
                    >
                      {needsAck ? 'فتح وإقرار' : 'فتح'}
                    </Button>
                  ) : null}

                  {needsAck ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={acknowledge.isPending}
                      onClick={() => acknowledge.mutate(item.id)}
                    >
                      إقرار
                    </Button>
                  ) : (
                    // Deliberately absent for unacknowledged critical alerts.
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={dismiss.isPending}
                      onClick={() => dismiss.mutate(item.id)}
                    >
                      تجاهل
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </aside>
    </>
  );
}
